import React from 'react';
import {UncontrolledPopover, PopoverHeader, PopoverBody} from 'reactstrap';
import {ScoreDonutSection} from "./ScoreWeightDonut";
import FilterableLocationList from './FilterableLocationList';

import labels from "../../sectionLabels"
import {
  reweightAll,
  avgScores,
} from "../../scoreWeightsWorker"

import {
  flattenScoreComponentsTree,
  scoreComponentToScoreItem,
} from "../../scoreWeights"
import {getColor} from "./colorForScorePath"
import * as scoreHierarchy from "../../scoreHierarchy";

import lodash from "lodash"
import {withInjector} from "../../../angularReactRedux/InjectorProvider";
import {ScoreLegend} from "./ScoreLegend";

import {CSSTransition} from 'react-transition-group'

const componentsToScoreItems = components => flattenScoreComponentsTree(components).map(scoreComponentToScoreItem)

/**
 * Shows overall info about the score set's weights and displays
 * 1) fetches the current score, or an estimate of the current score if the current score is not available,
 *    and shows it with a donut of the current weights
 * 2)
 *  - if the current score has not been edited: An info bubble
 *  - else: An estimate of what the score will be along a donut of the the new weights
 * 3) A legend of the weight's colors and the weights names
 **/
export class UninjectedScorePreviewSection extends React.Component {

  constructor(props) {
    super(props)
    this.state = {
      loadingScoreResults: true,
      currentScores: [],
      currentScoresIsApproximate: true,
      currentTotal: undefined,
      currentTotalIsLoading: true,
      currentWeights: [],
      updatedScores: [],
      updatedWeights: [],
      updatedTotal: undefined,
      updatedTotalIsLoading: true,
      countLocationsWithScore: undefined,
      countLocationsForConfig: undefined

  }
    this.recomputeEstimate = lodash.debounce(this.recomputeEstimate, 200)
  }

  componentDidUpdate(prevProps, prevState) {


    const idPath = 'scoreSet.scoreConfig._id.$oid';
    const revisionPath = 'scoreSet.scoreConfig.revision';
    /** refetch scores if the selected score config changes */
    if (lodash.get(prevProps, idPath) !== lodash.get(this.props, idPath)) {
      this.rebuildScoreAndApproximation()

    }
    /** scoreSet was saved. Reset current and updated weights and rebuild total if applicable */
    else if (lodash.get(prevProps, revisionPath) !== lodash.get(this.props, revisionPath)) {

      const weights = componentsToScoreItems(this.props.scoreSet.scoreConfig.components)
      this.setState({ currentWeights: weights, updatedWeights: weights, currentScoresIsApproximate: true })
      this.recomputeEstimate(weights)
      this.setCurrentTotal(this.state.currentScores, weights, true)


    }
    /** score results have been loaded from the API, recompute estimate score */
    else if (prevState.loadingScoreResults && !this.state.loadingScoreResults) {
      this.recomputeEstimate(this.state.updatedWeights)
    } else {

      let updatedWeights = componentsToScoreItems(this.props.scoreSet.scoreConfig.components);
      /** user modified weights: recompute estimate score */
      if (!lodash.isEqual(this.state.updatedWeights, updatedWeights)) {
        this.setState({ updatedWeights: updatedWeights })
        this.recomputeEstimate(updatedWeights)
      }
    }
  }

  recomputeEstimate(weights) {

    this.setState({ updatedTotalIsLoading: true });
    reweightAll(this.state.currentScores, weights, ['total']).then(reweighted => {
      this.setState({
        updatedTotal: reweighted[0].value,
        updatedTotalIsLoading: false
      })
    })

  }

  componentDidMount() {
    if (this.props.scoreSet) {
      this.rebuildScoreAndApproximation()
    }
  }

  /**
   * Fetches current scores from the API, and triggering generation of current and estimated new scores
   */
  rebuildScoreAndApproximation() {

    // store the initialScore set weights to apply to the scoreResults
    const scoreSet = this.props.scoreSet;
    let weights = componentsToScoreItems(scoreSet.scoreConfig.components);
    this.setState({
      loadingScoreResults: true,
      currentTotalIsLoading: true,
      currentWeights: weights,
      updatedTotalIsLoading: true,
      updatedWeights: weights
    })

    // ensure requests don't race. Ignore results from later ids
    this.fetchScoreApproximationId = (this.fetchScoreApproximationId || 0) + 1;
    let id = this.fetchScoreApproximationId;

    this.props.$injector.get('scoreResultsService').latestScores({
      scoreConfigId: scoreSet.scoreConfig._id.$oid,
      scoreConfigVersion: scoreSet.scoreConfig.revision,
      accountIds: scoreSet.accountIds.length ? scoreSet.accountIds : undefined,
      verticalIds: scoreSet.verticalIds.length ? scoreSet.verticalIds : [null],
      scorePaths: scoreHierarchy.withDescendants('total', 3)
    }).then(results => {
      if (id === this.fetchScoreApproximationId) {
        let currentScoresIsApproximate = !results.scoresMatchScoreConfig;
        this.setState({
          loadingScoreResults: false,
          currentScoresIsApproximate,
          currentScores: results.values,
          countLocationsWithScore: results.countLocationsWithScore,
          countLocationsForConfig: results.countLocationsForConfig
        })
        this.setCurrentTotal(results.values, weights, currentScoresIsApproximate)
      }
    })
  }

  setCurrentTotal(scores, weights, isApproximate) {
    this.setState({currentTotalIsLoading: true});
    (isApproximate ?
      reweightAll(scores, weights, ['total']) :
      avgScores(scores, ['total'])).then(avgs => {
        this.setState({
          currentTotal: avgs[0].value,
          currentTotalIsLoading: false
        })
    })
  }


  render() {

    if (!this.props.scoreSet.scoreConfig) {
      return <span></span>
    }

    const {
      currentTotal, currentTotalIsLoading, currentScoresIsApproximate, currentWeights,
      updatedTotal, updatedTotalIsLoading,                             updatedWeights,
      countLocationsWithScore, countLocationsForConfig
    } = this.state

    const {scoreSet, isPreviewMode, $injector, ...props} = this.props

    const hasVisibleApproximation = (isPreviewMode && !updatedTotalIsLoading && updatedTotal !== undefined) ||
    (currentScoresIsApproximate && currentTotal !== undefined && !currentTotalIsLoading)

    let locationPopOver;

    if (!isPreviewMode) {
      locationPopOver = (
        <span>
            <a className="clickable" id="locations-popover" style={{textDecoration: "underline"}}><span className="ss-icon ss-location" /> {this.props.scoreSet.locationIds.length} Locations</a>
            <UncontrolledPopover placement="bottom" target={"locations-popover"} className="popover-custom locations">
              <PopoverHeader>
                Locations
              </PopoverHeader>
              <PopoverBody>
                <FilterableLocationList locationsAll={this.props.locations} locationsSelected={this.props.scoreSet.locationIds} />
              </PopoverBody>
            </UncontrolledPopover>
        </span>
      );
    }
    return (
      <div {...props}>
        <div className={`score-preview-section ${isPreviewMode ? 'preview-mode' : ''}`} >
          <div style={{flex: isPreviewMode ? 0 : 2}} className={isPreviewMode ? 'removed' : ''}>
            <CSSTransition
              classNames="info-slide"
              timeout={1000}
              in={!isPreviewMode}
            >
              <div
                className="info-slide"
              >
                <p
                  style={{margin: 0, textAlign: 'justify'}}
                >
                  {/*<span style={{float: 'left', padding: '10px 15px 0 10px', fontSize: '24px'}} className="ss ss-info"/>*/}
                  The LBV score is an aggregate of the most important performance metrics tied to
                  reputation (<ScoreCategory scorePath="reviews"/>), social media
                  activity (<ScoreCategory scorePath="social"/>) and local
                  search (<ScoreCategory scorePath="listings"/> and <ScoreCategory scorePath="rankings"/>).
                  Customer activity levels, listing accuracy, rankings, customer ratings and
                  your <ScoreCategory scorePath="competitors"/> all factor into the score. In general, a score above 70
                  is reflective of an industry leader, a score between 50 and 70 is average and a score below 50 is poor.
                </p>
              </div>
            </CSSTransition>
            <div
              style={{
                transition: 'max-height 1000ms',
                maxHeight: countLocationsForConfig === 0 && !isPreviewMode ? '300px': '0',
                overflow: 'hidden'
              }}
            >
              <div className="alert alert-warning">
                <span className="ss ss-warning ss-warn ss-alert ss-triangle" /> This score template's accounts or industries do not match any locations, or another score config for the same account or industry has higher precedence.
              </div>
            </div>
          </div>
          {/* TODO - show location counts. Show warning icon if this config doesn't match any locations*/}
          <ScoreDonutSection
            title={isPreviewMode ? 'Previous Settings' : 'Current Settings'}
            tooltip={
              currentTotal === undefined ?
                'No score: Score has not been run yet for any locations.' :
                currentScoresIsApproximate ?
                  'The current revisions to this score template have not finished running for all locations. This score is an estimate based on previous scores. Actual scores may vary once final calculations are run.' :
                  `Current average score for locations that use this score template. Based off of ${countLocationsWithScore} locations.`
            }
            totalScore={currentTotal}
            totalScoreIsLoading={currentTotalIsLoading}
            totalScoreIsEstimate={currentScoresIsApproximate}
            scoreItems={scoreHierarchy.filterTopLevelCategories(currentWeights)}
            footer={!isPreviewMode && hasVisibleApproximation ? '* Estimated Score' : ''}
            showOutOf100={!isPreviewMode}
          />
          <div
            className={`align-content-vertically ${isPreviewMode ? '' : 'removed'}`}
            style={{
              flex: isPreviewMode ? 0.25 : 0,
              fontSize: '32px',
              textAlign: 'center',
          }}><span className="far fa-arrow-right" /></div>
          <ScoreDonutSection
            title="New Settings"
            style={{flex: isPreviewMode ? 1 : 0}}
            className={isPreviewMode ? '' : 'removed'}
            tooltip={
              updatedTotal === undefined ?
                'No score preview available until scoring has been run.' :
                'This score is an approximation based on the current settings and the most recent scores for all locations in this score template. Actual score may vary once final calculations are run.'
            }
            totalScore={updatedTotal}
            totalScoreIsLoading={updatedTotalIsLoading}
            totalScoreIsEstimate={true}
            scoreItems={scoreHierarchy.filterTopLevelCategories(updatedWeights)}
            footer={isPreviewMode && hasVisibleApproximation ? <span>* Estimated Score</span> : undefined}
          />
          <div className="align-content-vertically">
            <ScoreLegend
              style={{marginRight: '10px'}}
              isVertical={true}
              scorePaths={scoreHierarchy.filterTopLevelCategories(this.state.updatedWeights).map(x => x.scorePath)}/>
          </div>
        </div>
        <div className="row d-flex justify-content-end">
          <div className="text-center mt-1 col-6 col-md-4">
            {locationPopOver}
          </div>
        </div>
      </div>

    );
  }
}

export const ScorePreviewSection = withInjector(UninjectedScorePreviewSection)

const ScoreCategory = ({scorePath}) => {
  return <span style={{fontWeight: '700', color: getColor(scorePath)}}>{labels[scorePath]}</span>
}
